<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from "vue";
import { api, errorText, dateTime, useSession } from "../api";
import { LinkIcon, AddIcon, CopyIcon, CheckIcon, RefreshIcon } from "../icons";
import McpPermissions from "../components/McpPermissions.vue";
import ClientBrand from "../components/ClientBrand.vue";
import AiPolicy from "../components/AiPolicy.vue";
import "../styles/mcp.scss";
const session = useSession();
type Connection = {
  id: string;
  name: string;
  client_brand: string | null;
  kind: string;
  scopes: string[];
  dataset_ids: string[] | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
};
const settings = ref<{
  url: string;
  scopes: string[];
  canManage: boolean;
  connections: Connection[];
}>();
const datasets = ref<{ id: string; name: string }[]>([]),
  loading = ref(true),
  error = ref(""),
  notice = ref(""),
  busy = ref(false);
const creating = ref(false),
  name = ref(""),
  scopes = ref(["data:read", "data:write"]),
  datasetIds = ref<string[] | null>(null),
  expires = ref<number | null>(null);
const created = ref<{ token: string; name: string }>(),
  copied = ref(""),
  revokeId = ref(""),
  testing = ref(false),
  tested = ref(false),
  nameInput = ref<HTMLInputElement>();
const client = ref("url");
const clientBrand = ref<string | null>(null);
const allowed = computed(() =>
  settings.value?.canManage
    ? ["data:read", "data:write", "data:manage"]
    : ["data:read", "data:write"],
);
const valid = computed(
  () =>
    !!name.value.trim() &&
    scopes.value.length > 0 &&
    (datasetIds.value === null || datasetIds.value.length > 0),
);
const configuration = computed(() => {
  const url = settings.value?.url ?? "";
  if (client.value === "vscode")
    return JSON.stringify(
      { servers: { yawhub: { type: "http", url } } },
      null,
      2,
    );
  if (client.value === "json")
    return JSON.stringify(
      {
        mcpServers: {
          yawhub: {
            url,
            ...(created.value
              ? { headers: { Authorization: "Bearer " + created.value.token } }
              : {}),
          },
        },
      },
      null,
      2,
    );
  return url;
});
const scopeLabel = (values: string[]) =>
  values
    .map(
      (s) =>
        ({
          "data:read": "Læs",
          "data:write": "Skriv",
          "data:manage": "Administrér",
        })[s],
    )
    .join(" · ");
const expired = (c: Connection) =>
  !!c.expires_at && new Date(c.expires_at).valueOf() < Date.now();
async function load() {
  loading.value = true;
  error.value = "";
  try {
    [settings.value, datasets.value] = await Promise.all([
      api("/api/mcp"),
      api("/api/datasets?status=all"),
    ]);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}
onMounted(load);
watch(
  () => session.organization,
  () => {
    created.value = undefined;
    creating.value = false;
    settings.value = undefined;
    datasets.value = [];
    datasetIds.value = null;
    void load();
  },
);
async function copy(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    copied.value = label;
    notice.value = "Kopieret til udklipsholderen.";
  } catch {
    error.value = "Kunne ikke kopiere. Markér teksten og kopiér den manuelt.";
  }
}
async function openForm() {
  creating.value = true;
  await nextTick();
  nameInput.value?.focus();
}
async function createKey() {
  busy.value = true;
  error.value = "";
  try {
    const result = await api("/api/mcp/keys", {
      method: "POST",
      body: {
        name: name.value,
        clientBrand: clientBrand.value,
        scopes: scopes.value,
        datasetIds: datasetIds.value,
        expiresInDays: expires.value,
      },
    });
    created.value = { token: result.token, name: result.connection.name };
    tested.value = false;
    creating.value = false;
    name.value = "";
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function revoke(id: string) {
  busy.value = true;
  error.value = "";
  try {
    await api("/api/mcp/connections/" + id, { method: "DELETE" });
    revokeId.value = "";
    notice.value = "Adgangen er tilbagekaldt.";
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function testKey() {
  if (!created.value || !settings.value) return;
  testing.value = true;
  error.value = "";
  try {
    const r = await fetch(settings.value.url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json, text/event-stream",
        authorization: "Bearer " + created.value.token,
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "initialize",
        params: {
          protocolVersion: "2025-11-25",
          capabilities: {},
          clientInfo: { name: "Yawhub connection test", version: "1.0" },
        },
      }),
    });
    const body = await r.json();
    if (!r.ok || body.error || !body.result?.serverInfo)
      throw new Error(
        body.message ??
          body.error?.message ??
          "Forbindelsen kunne ikke bekræftes.",
      );
    tested.value = true;
    notice.value = "Forbindelsen virker. Din nøgle kan bruges i en MCP-klient.";
  } catch (e) {
    error.value = errorText(e);
  } finally {
    testing.value = false;
  }
}
</script>
<template>
  <section class="page-heading">
    <div>
      <h1>Forbind din AI til dine data.</h1>
      <p class="subtitle">
        Læs, analysér og opdatér dit workspace fra din foretrukne AI-klient.
      </p>
    </div>
  </section>
  <p v-if="error" class="error" role="alert">
    {{ error }} <button class="text-button" @click="load">Prøv igen</button>
  </p>
  <p v-if="notice" class="mcp-feedback" role="status">{{ notice }}</p>
  <div v-if="loading && !settings" class="empty-state" role="status">
    Henter dine forbindelser…
  </div>
  <div v-else-if="settings" class="mcp-layout">
    <section class="mcp-setup" aria-labelledby="connect-title">
      <div class="mcp-section-heading">
        <LinkIcon :size="22" />
        <h2 id="connect-title">Én adresse. Dit workspace.</h2>
      </div>
      <p>
        Tilføj adressen som en ekstern MCP-server i din AI-klient. Log ind på
        Yawhub, og vælg, hvad klienten må få adgang til.
      </p>
      <label for="mcp-url" class="mcp-label">Serveradresse</label>
      <div class="mcp-copy-row">
        <input
          id="mcp-url"
          :value="settings.url"
          readonly
          @focus="($event.target as HTMLInputElement).select()"
        /><button class="button primary" @click="copy(settings.url, 'url')">
          <CheckIcon v-if="copied === 'url'" :size="17" /><CopyIcon
            v-else
            :size="17"
          />{{ copied === "url" ? "Kopieret" : "Kopiér" }}
        </button>
      </div>
      <ol class="mcp-steps">
        <li>Åbn forbindelser eller MCP-servere i din AI-klient.</li>
        <li>Indsæt adressen, og vælg at forbinde med login.</li>
        <li>Godkend adgangen. Dine datasæt er nu klar til din AI.</li>
      </ol>
      <div class="ai-client-brands" aria-label="AI-klienter">
        <span v-for="brand in ['chatgpt', 'claude', 'gemini']" :key="brand"
          ><ClientBrand :brand="brand" />{{
            { chatgpt: "ChatGPT", claude: "Claude", gemini: "Gemini" }[brand]
          }}</span
        >
      </div>
      <p class="muted small">
        Brug en klient med understøttelse af eksterne MCP-servere.
        Tilgængelighed afhænger af klienten og dit abonnement.
      </p>
      <details class="mcp-details">
        <summary>Opsætning med JSON</summary>
        <label class="mcp-label"
          >Format<select v-model="client">
            <option value="url">Kun serveradresse</option>
            <option value="json">mcpServers (URL og eventuel nøgle)</option>
            <option value="vscode">VS Code · mcp.json</option>
          </select></label
        >
        <pre class="mcp-code">{{ configuration }}</pre>
        <button class="button secondary" @click="copy(configuration, 'config')">
          <CopyIcon :size="16" />Kopiér opsætning
        </button>
        <p class="muted small">
          Vælg HTTP / Streamable HTTP, hvis klienten spørger om transport.
        </p>
      </details>
    </section>
    <aside class="mcp-guide">
      <h2>Prøv det i din AI</h2>
      <p class="mcp-example">
        “Vis mine datasæt, og hjælp mig med at finde udviklingen i mine data.”
      </p>
      <p class="mcp-example">
        “Gem disse data i Yawhub. Brug det eksisterende datasæt, hvis felterne
        passer.”
      </p>
      <h3>Data begge veje</h3>
      <p>
        Hent alle sider, filtrér og beregn resultater. Send op til 500 rækker ad
        gangen, og fortsæt med næste batch.
      </p>
      <p class="muted small">
        Din AI får altid højst den adgang, du selv har. Du kan tilbagekalde
        forbindelsen her.
      </p>
    </aside>
    <section class="mcp-connections" aria-labelledby="connections-title">
      <div class="mcp-section-heading mcp-spread">
        <div>
          <h2 id="connections-title">Dine forbindelser</h2>
          <p class="muted">
            Login-forbindelser og adgangsnøgler samlet ét sted.
          </p>
        </div>
        <button
          class="button secondary"
          :disabled="creating || !!created"
          @click="openForm"
        >
          <AddIcon :size="17" />Opret adgangsnøgle
        </button>
      </div>
      <div
        v-if="created"
        class="mcp-secret"
        role="region"
        aria-label="Din nye adgangsnøgle"
      >
        <h3>{{ created.name }} er klar</h3>
        <p>Kopiér nøglen nu. Den vises kun denne ene gang.</p>
        <div class="mcp-copy-row">
          <textarea
            :value="created.token"
            readonly
            aria-label="Ny adgangsnøgle"
            rows="2"
          /><button class="button primary" @click="copy(created.token, 'key')">
            <CopyIcon :size="17" />Kopiér nøgle
          </button>
        </div>
        <p class="muted small">
          Indsæt den som Bearer-token eller i Authorization-headeren. Vælg
          mcpServers under JSON-opsætning for at kopiere en konfiguration med
          nøglen.
        </p>
        <div class="mcp-actions">
          <button class="button secondary" :disabled="testing" @click="testKey">
            <CheckIcon v-if="tested" :size="17" /><RefreshIcon
              v-else
              :size="17"
            />{{
              testing
                ? "Tester…"
                : tested
                  ? "Forbindelse bekræftet"
                  : "Test forbindelse"
            }}</button
          ><button class="text-button" @click="created = undefined">
            Jeg har gemt nøglen
          </button>
        </div>
      </div>
      <form v-if="creating" class="mcp-key-form" @submit.prevent="createKey">
        <h3>Adgangsnøgle til en klient eller et script</h3>
        <p class="muted">
          Brug en nøgle, når din klient understøtter en Authorization-header.
        </p>
        <label class="mcp-label"
          >Navn<input
            ref="nameInput"
            v-model="name"
            required
            maxlength="120"
            placeholder="Fx min AI-assistent" /></label
        ><label class="mcp-label"
          >AI-klient <span class="muted">(valgfrit)</span
          ><select v-model="clientBrand">
            <option :value="null">Anden klient eller script</option>
            <option value="chatgpt">ChatGPT</option>
            <option value="claude">Claude</option>
            <option value="gemini">Gemini</option>
          </select></label
        ><McpPermissions
          v-model:scopes="scopes"
          v-model:dataset-ids="datasetIds"
          :datasets="datasets"
          :allowed="allowed"
        /><label class="mcp-label"
          >Udløb<select v-model="expires">
            <option :value="null">Intet automatisk udløb</option>
            <option :value="30">Efter 30 dage</option>
            <option :value="90">Efter 90 dage</option>
            <option :value="365">Efter 1 år</option>
          </select></label
        >
        <div class="mcp-actions">
          <button class="button primary" :disabled="busy || !valid">
            {{ busy ? "Opretter…" : "Opret nøgle" }}</button
          ><button
            class="button secondary"
            type="button"
            :disabled="busy"
            @click="creating = false"
          >
            Annullér
          </button>
        </div>
      </form>
      <p
        v-if="!settings.connections.length && !creating && !created"
        class="mcp-empty"
      >
        Ingen forbindelser endnu. Tilføj serveradressen i din AI-klient, eller
        opret en adgangsnøgle til dit første script.
      </p>
      <ul v-else-if="settings.connections.length" class="mcp-connection-list">
        <li
          v-for="c in settings.connections"
          :key="c.id"
          class="mcp-connection"
        >
          <div>
            <strong class="connection-name"
              ><ClientBrand :brand="c.client_brand" :name="c.name" />{{
                c.name
              }}</strong
            >
            <p class="small muted">
              {{ c.kind === "oauth" ? "Forbundet med login" : "Adgangsnøgle" }}
              · {{ scopeLabel(c.scopes) }} ·
              {{
                c.dataset_ids
                  ? c.dataset_ids.length + " valgte datasæt"
                  : "Hele workspace"
              }}
            </p>
            <p class="small muted">
              {{
                c.last_used_at
                  ? "Senest brugt " + dateTime(c.last_used_at)
                  : "Ikke brugt endnu"
              }}{{ c.expires_at ? " · Udløber " + dateTime(c.expires_at) : "" }}
            </p>
          </div>
          <span v-if="c.revoked_at" class="muted small">Tilbagekaldt</span
          ><span v-else-if="expired(c)" class="muted small">Udløbet</span>
          <div v-else-if="revokeId === c.id" class="mcp-revoke">
            <p class="small">Afbryd denne forbindelses adgang?</p>
            <button
              class="button destructive"
              :disabled="busy"
              @click="revoke(c.id)"
            >
              Tilbagekald</button
            ><button
              class="text-button"
              :disabled="busy"
              @click="revokeId = ''"
            >
              Annullér
            </button>
          </div>
          <button v-else class="text-button" @click="revokeId = c.id">
            Tilbagekald adgang
          </button>
        </li>
      </ul>
    </section>
  </div>
  <AiPolicy />
</template>
